/**
 * Shared volunteer fallback-image helpers.
 *
 * Provides deterministic fallback image selection for volunteer event images.
 * Used by the search indexer (to bake a fallback into indexed documents) and
 * by the frontend rendering layer (as the fallback for unsafe/absent URLs).
 *
 * Note: the `next/image` host-safety guard lives in the rendering layer
 * (`@sola/ark-skippy` volunteer-utils), not here — the search SDK is
 * domain-agnostic and does not know about website rendering constraints.
 */

/** Fallback image filenames (WebP, served from Next.js public folder). */
export const VOLUNTEER_FALLBACK_IMAGES = [
  'GenericConcert1.webp',
  'GenericConcert2.webp',
  'GenericConcert5.webp',
] as const;

/** Root-relative base path for volunteer images in the public folder. */
export const VOLUNTEER_IMAGE_PATH = '/images/volunteers';

/** Deterministic hash: sum of char codes, mod list length. */
const charCodeHash = (input: string, mod: number): number => {
  const sum = [...input].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return sum % mod;
};

/**
 * Select a deterministic fallback image URL based on a stable key (e.g. slug).
 * Returns a root-relative path suitable for Next.js public folder serving.
 */
export const selectFallbackImage = (key: string): { url: string } => {
  const file =
    VOLUNTEER_FALLBACK_IMAGES[
      charCodeHash(key, VOLUNTEER_FALLBACK_IMAGES.length)
    ];
  return { url: `${VOLUNTEER_IMAGE_PATH}/${file}` };
};
